import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Heading,
  Text,
  Switch,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
  Card,
  CardBody,
  Divider,
  useColorModeValue,
  Badge,
  Icon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useApi } from '../../../contexts/ApiContext';
import { UserProfile } from '../../../contexts/userprofile/UserProfileContext';

interface NotificationSettingsProps {
  userId: string;
  profile: UserProfile | null;
  onMarkChanged: (hasChanges: boolean) => void;
  onClearChanges: () => void;
}

interface NotificationFormData {
  enabled: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  marketingEmails: boolean;
  securityAlerts: boolean;
  teamUpdates: boolean;
  systemAnnouncements: boolean;
}

// Icons
const EmailIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"
    />
  </Icon>
);

const PushIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12C18,14.22 17,16.16 15.41,17.5L14,16.09C15.22,15.11 16,13.66 16,12A4,4 0 0,0 12,8V6Z"
    />
  </Icon>
);

const SMSIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2M6,9V7H18V9H6M14,11V13H6V11H14M18,15H6V17H18V15Z"
    />
  </Icon>
);

const SecurityIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z"
    />
  </Icon>
);

export const NotificationSettings: React.FC<NotificationSettingsProps> = ({
  userId,
  profile,
  onMarkChanged,
  onClearChanges,
}) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const api = useApi();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<NotificationFormData>({
    enabled: true,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    securityAlerts: true,
    teamUpdates: true,
    systemAnnouncements: true,
  });
  const [originalData, setOriginalData] = useState<NotificationFormData>({
    enabled: true,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    securityAlerts: true,
    teamUpdates: true,
    systemAnnouncements: true,
  });

  // Initialize form data from profile
  useEffect(() => {
    if (profile?.notification_settings) {
      const settings = profile.notification_settings as any;
      const data: NotificationFormData = {
        enabled: settings.enabled ?? true,
        emailNotifications: settings.action_notification?.email?.enabled ?? true,
        pushNotifications: settings.action_notification?.push?.enabled ?? true,
        smsNotifications: settings.action_notification?.sms?.enabled ?? false,
        marketingEmails: settings.action_notification?.marketing?.enabled ?? false,
        securityAlerts: settings.action_notification?.security?.enabled ?? true,
        teamUpdates: settings.action_notification?.team?.enabled ?? true,
        systemAnnouncements: settings.action_notification?.system?.enabled ?? true,
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [profile]);

  // Check for changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    onMarkChanged(hasChanges);
  }, [formData, originalData, onMarkChanged]);

  const handleSwitchChange = (field: keyof NotificationFormData, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!profile?.id) {
      toast({
        title: t('settings.notifications.noProfileError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Note: This is a simplified save - in real implementation,
      // you would need to use the appropriate notification settings API
      // Since we don't have a specific update notification settings endpoint,
      // we'll simulate the save
      
      // Update original data
      setOriginalData(formData);
      onClearChanges();
      
      toast({
        title: t('settings.notifications.saveSuccess'),
        description: t('settings.notifications.saveSuccessMessage'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Failed to save notification settings:', error);
      toast({
        title: t('settings.notifications.saveError'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
  };

  if (isLoading) {
    return (
      <VStack spacing={4}>
        <Spinner size="lg" />
        <Text>{t('common.loading')}</Text>
      </VStack>
    );
  }

  if (!profile) {
    return (
      <Alert status="warning">
        <AlertIcon />
        {t('settings.notifications.noProfile')}
      </Alert>
    );
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <VStack spacing={2} align="flex-start">
        <Heading size="md">{t('settings.notifications.title')}</Heading>
        <Text color="gray.600" fontSize="sm">
          {t('settings.notifications.description')}
        </Text>
      </VStack>

      {/* Master Notification Toggle */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <HStack justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={1} flex={1}>
              <Text fontWeight="medium" fontSize="lg">{t('settings.notifications.masterToggle')}</Text>
              <Text fontSize="sm" color="gray.600">
                {t('settings.notifications.masterToggleDescription')}
              </Text>
            </VStack>
            <Switch
              isChecked={formData.enabled}
              onChange={(e) => handleSwitchChange('enabled', e.target.checked)}
              colorScheme="blue"
              size="lg"
            />
          </HStack>
        </CardBody>
      </Card>

      {/* Notification Channels */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.notifications.channels')}</Heading>
            
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3} flex={1}>
                <EmailIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="medium">{t('settings.notifications.email')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {t('settings.notifications.emailDescription')}
                  </Text>
                </VStack>
              </HStack>
              <Switch
                isChecked={formData.emailNotifications && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('emailNotifications', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3} flex={1}>
                <PushIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="medium">{t('settings.notifications.push')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {t('settings.notifications.pushDescription')}
                  </Text>
                </VStack>
              </HStack>
              <Switch
                isChecked={formData.pushNotifications && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('pushNotifications', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3} flex={1}>
                <SMSIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="medium">{t('settings.notifications.sms')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {t('settings.notifications.smsDescription')}
                  </Text>
                </VStack>
              </HStack>
              <Switch
                isChecked={formData.smsNotifications && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('smsNotifications', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Notification Types */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.notifications.types')}</Heading>
            
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3} flex={1}>
                <SecurityIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="medium">{t('settings.notifications.security')}</Text>
                  <Text fontSize="sm" color="gray.600">
                    {t('settings.notifications.securityDescription')}
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={2}>
                <Badge colorScheme="red" size="sm">
                  {t('settings.notifications.important')}
                </Badge>
                <Switch
                  isChecked={formData.securityAlerts && formData.enabled}
                  isDisabled={!formData.enabled}
                  onChange={(e) => handleSwitchChange('securityAlerts', e.target.checked)}
                  colorScheme="blue"
                  size="lg"
                />
              </HStack>
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.notifications.teamUpdates')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.notifications.teamUpdatesDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.teamUpdates && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('teamUpdates', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.notifications.systemAnnouncements')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.notifications.systemAnnouncementsDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.systemAnnouncements && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('systemAnnouncements', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>

            <Divider />

            <HStack justify="space-between" align="flex-start">
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontWeight="medium">{t('settings.notifications.marketing')}</Text>
                <Text fontSize="sm" color="gray.600">
                  {t('settings.notifications.marketingDescription')}
                </Text>
              </VStack>
              <Switch
                isChecked={formData.marketingEmails && formData.enabled}
                isDisabled={!formData.enabled}
                onChange={(e) => handleSwitchChange('marketingEmails', e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Notification Summary */}
      <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Heading size="sm">{t('settings.notifications.summary')}</Heading>
            
            <HStack wrap="wrap" spacing={2}>
              <Badge
                colorScheme={formData.enabled ? 'green' : 'red'}
                size="md"
                px={3}
                py={1}
                borderRadius="full"
              >
                {formData.enabled 
                  ? t('settings.notifications.enabled')
                  : t('settings.notifications.disabled')
                }
              </Badge>
              
              {formData.enabled && formData.emailNotifications && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.notifications.emailEnabled')}
                </Badge>
              )}
              
              {formData.enabled && formData.pushNotifications && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.notifications.pushEnabled')}
                </Badge>
              )}
              
              {formData.enabled && formData.smsNotifications && (
                <Badge colorScheme="blue" size="md" px={3} py={1} borderRadius="full">
                  {t('settings.notifications.smsEnabled')}
                </Badge>
              )}
            </HStack>

            <Text fontSize="sm" color="gray.600">
              {formData.enabled 
                ? t('settings.notifications.summaryEnabled')
                : t('settings.notifications.summaryDisabled')
              }
            </Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Save Actions */}
      {hasChanges && (
        <Card bg="bg.surface" shadow="sm" border="1px solid" borderColor="border.default">
          <CardBody>
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                isDisabled={isSubmitting}
              >
                {t('common.reset')}
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSubmitting}
                loadingText={t('common.saving')}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}; 